import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star, Eye, EyeOff, Trash2, MessageSquare } from "lucide-react";

const formatDate = (d) => d ? new Date(d).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";
const ratingColors = { 5: "bg-green-900/30 text-green-400 border-green-800/30", 4: "bg-blue-900/30 text-blue-400 border-blue-800/30", 3: "bg-yellow-900/30 text-yellow-400 border-yellow-800/30", 2: "bg-orange-900/30 text-orange-400 border-orange-800/30", 1: "bg-red-900/30 text-red-400 border-red-800/30" };

export default function AdminTestimonialPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const fetch = useCallback(async () => {
    try { const { data } = await api.get("/testimonial/admin"); setTestimonials(data); } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const toggleVisibility = async (tid) => {
    try { await api.put(`/testimonial/${tid}/visibility`); fetch(); toast.success("Visibilitas diperbarui"); } catch {}
  };

  const deleteTestimonial = async (tid) => {
    if (!window.confirm("Hapus testimonial ini?")) return;
    await api.delete(`/testimonial/${tid}`); fetch(); toast.success("Dihapus");
  };

  const stats = {
    total: testimonials.length,
    five: testimonials.filter(t => t.rating === 5).length,
    visible: testimonials.filter(t => t.is_visible).length,
    avg: testimonials.length ? (testimonials.reduce((a, t) => a + t.rating, 0) / testimonials.length).toFixed(1) : 0,
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-[#E60000] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 md:p-8" data-testid="admin-testimonial-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-white">Testimonial</h1>
        <p className="text-gray-400 text-sm mt-1">Kelola testimonial dari pelanggan</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total", val: stats.total },
          { label: "Bintang 5", val: stats.five },
          { label: "Ditampilkan", val: stats.visible },
          { label: "Rata-rata", val: stats.avg },
        ].map((s, i) => (
          <div key={i} className="bg-[#0F0F0F] border border-[#262626] p-4 text-center">
            <p className="font-heading text-2xl font-bold text-white">{s.val}</p>
            <p className="text-xs text-gray-500 uppercase font-semibold">{s.label}</p>
          </div>
        ))}
      </div>

      {testimonials.length === 0 ? (
        <div className="text-center py-16 bg-[#0F0F0F] border border-[#262626]">
          <MessageSquare size={48} className="text-gray-700 mx-auto mb-4" />
          <p className="text-gray-400">Belum ada testimonial</p>
        </div>
      ) : (
        <div className="bg-[#0F0F0F] border border-[#262626] overflow-x-auto" data-testid="testimonial-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#262626]">
                <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Pelanggan</th>
                <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Rating</th>
                <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Pesan</th>
                <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Kode Transaksi</th>
                <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Tanggal</th>
                <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Status</th>
                <th className="text-right px-5 py-3 text-xs font-bold uppercase text-gray-500">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {testimonials.map(t => (
                <tr key={t.id} className="border-b border-[#262626]/50 hover:bg-[#1A1A1A] cursor-pointer" onClick={() => setDetail(t)} data-testid={`testi-row-${t.id}`}>
                  <td className="px-5 py-3">
                    <p className="text-white font-semibold">{t.name}</p>
                    <p className="text-gray-500 text-xs">{t.service}</p>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map(s => (
                        <Star key={s} size={12} className={s <= t.rating ? "text-yellow-400 fill-current" : "text-gray-700"} />
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-gray-400 max-w-[200px] truncate">{t.message}</td>
                  <td className="px-5 py-3"><span className="font-mono text-xs text-gray-400">{t.transaction_code}</span></td>
                  <td className="px-5 py-3 text-gray-500 text-xs">{formatDate(t.created_at)}</td>
                  <td className="px-5 py-3">
                    <Badge className={`border text-[10px] ${t.is_visible ? "bg-green-900/30 text-green-400 border-green-800/30" : "bg-gray-700/30 text-gray-400 border-gray-600/30"}`}>
                      {t.is_visible ? "Tampil" : "Hidden"}
                    </Badge>
                    {t.rating === 5 && <Badge className="ml-1 bg-yellow-900/20 text-yellow-400 border border-yellow-800/30 text-[9px]">Publik</Badge>}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                      <button onClick={() => toggleVisibility(t.id)} className={`p-1.5 transition-colors ${t.is_visible ? "text-green-400 hover:text-green-300" : "text-gray-600 hover:text-gray-400"}`} title={t.is_visible ? "Sembunyikan" : "Tampilkan"} data-testid={`toggle-vis-${t.id}`}>
                        {t.is_visible ? <Eye size={14} /> : <EyeOff size={14} />}
                      </button>
                      <button onClick={() => deleteTestimonial(t.id)} className="p-1.5 text-gray-600 hover:text-red-400" data-testid={`del-testi-${t.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="bg-[#0F0F0F] border-[#262626] text-white max-w-md rounded-none">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl font-bold uppercase">Detail Testimonial</DialogTitle>
                <DialogDescription className="text-gray-400 text-sm">Dari: {detail.name}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4" data-testid="testi-detail">
                <div className="flex items-center gap-2">
                  {[1,2,3,4,5].map(s => (
                    <Star key={s} size={20} className={s <= detail.rating ? "text-yellow-400 fill-current" : "text-gray-700"} />
                  ))}
                  <Badge className={`border text-xs ml-2 ${ratingColors[detail.rating] || ""}`}>{detail.rating}/5</Badge>
                </div>
                <div className="bg-[#0A0A0A] border border-[#1A1A1A] p-4">
                  <p className="text-gray-300 text-sm whitespace-pre-line leading-relaxed">"{detail.message}"</p>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><p className="text-gray-500 uppercase font-bold">Layanan</p><p className="text-white mt-0.5">{detail.service || "-"}</p></div>
                  <div><p className="text-gray-500 uppercase font-bold">Kode Transaksi</p><p className="text-white font-mono mt-0.5">{detail.transaction_code}</p></div>
                  <div><p className="text-gray-500 uppercase font-bold">Tanggal</p><p className="text-white mt-0.5">{formatDate(detail.created_at)}</p></div>
                  <div><p className="text-gray-500 uppercase font-bold">Status</p><Badge className={`mt-0.5 border text-[10px] ${detail.is_visible ? "bg-green-900/30 text-green-400 border-green-800/30" : "bg-gray-700/30 text-gray-400 border-gray-600/30"}`}>{detail.is_visible ? "Tampil" : "Hidden"}</Badge></div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
